import { useEffect, useState, useRef, useCallback, useMemo, type KeyboardEvent as ReactKE } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import { useAuthStore } from '@/stores/auth.store';
import { getScopedProfile, isPathAllowed } from '@/lib/scopedAccess';
import {
  Search,
  Users,
  CreditCard,
  ListChecks,
  LayoutDashboard,
  Wifi,
  Radio,
  Monitor,
  HelpCircle,
  Bell,
  BarChart3,
  FileText,
  Settings,
  Plus,
  Play,
  Timer,
  Package,
  Globe,
  ArrowRight,
  Loader2,
  User,
  X,
} from 'lucide-react';

// ─── Types ───
interface NavResult {
  id: string;
  label: string;
  description?: string;
  icon: React.ElementType;
  href: string;
  section: 'navigation';
}

interface SubscriberResult {
  id: string;
  label: string;
  description: string;
  icon: React.ElementType;
  href: string;
  section: 'subscribers';
  status?: string;
}

type Result = NavResult | SubscriberResult;

// ─── Navigation Items ───
const NAV_ITEMS: NavResult[] = [
  { id: 'nav-dashboard', label: 'Dashboard', description: 'Overview & KPIs', icon: LayoutDashboard, href: '/dashboard', section: 'navigation' },
  { id: 'nav-subscribers', label: 'Subscribers', description: 'Manage customers', icon: Users, href: '/subscribers', section: 'navigation' },
  { id: 'nav-subscriber-new', label: 'Add New Subscriber', description: 'Create customer account', icon: Plus, href: '/subscribers/new', section: 'navigation' },
  { id: 'nav-billing', label: 'Invoices & Billing', description: 'View invoices & payments', icon: CreditCard, href: '/billing', section: 'navigation' },
  { id: 'nav-plans', label: 'Plans', description: 'Internet plan management', icon: ListChecks, href: '/plans', section: 'navigation' },
  { id: 'nav-nas', label: 'NAS / Routers', description: 'MikroTik / RADIUS NAS', icon: Wifi, href: '/nas', section: 'navigation' },
  { id: 'nav-olt', label: 'OLT / GPON', description: 'Fiber OLT & ONU devices', icon: Radio, href: '/olt', section: 'navigation' },
  { id: 'nav-noc', label: 'NOC', description: 'Network alerts & monitoring', icon: Monitor, href: '/noc', section: 'navigation' },
  { id: 'nav-pppoe', label: 'PPPoE Sessions', description: 'Live session monitoring', icon: Wifi, href: '/pppoe', section: 'navigation' },
  { id: 'nav-tickets', label: 'Tickets', description: 'Customer support tickets', icon: HelpCircle, href: '/tickets', section: 'navigation' },
  { id: 'nav-reports', label: 'Reports', description: 'Revenue & analytics', icon: BarChart3, href: '/reports', section: 'navigation' },
  { id: 'nav-logs', label: 'Audit Logs', description: 'Activity history', icon: FileText, href: '/logs', section: 'navigation' },
  { id: 'nav-billing-auto', label: 'Billing Automation', description: 'Schedule & run billing', icon: Timer, href: '/billing/automation', section: 'navigation' },
  { id: 'nav-payment-gateway', label: 'Payment Gateway', description: 'Razorpay configuration & orders', icon: CreditCard, href: '/billing/gateway', section: 'navigation' },
  { id: 'nav-inventory', label: 'Inventory', description: 'Stock, job materials and purchasing', icon: Package, href: '/inventory', section: 'navigation' },
  { id: 'nav-inventory-assets', label: 'Subscriber Assets', description: 'Devices at customers, equipment recovery, POP racks, RMA', icon: Package, href: '/inventory/assets/subscribers', section: 'navigation' },
  { id: 'nav-inventory-transfers', label: 'Stock Transfers', description: 'Send stock between stores and count it in', icon: Package, href: '/inventory/stock/transfers', section: 'navigation' },
  { id: 'nav-inventory-audit', label: 'Stock Audit', description: 'Count the shelves by scanning and authorise adjustments', icon: Package, href: '/inventory/stock/audits', section: 'navigation' },
  { id: 'nav-inventory-reports', label: 'Inventory Reports', description: 'Stock, movement, valuation, cable consumption, audits', icon: Package, href: '/inventory/reports', section: 'navigation' },
  { id: 'nav-ipam', label: 'IP Management', description: 'IP pools & allocations', icon: Globe, href: '/ipam', section: 'navigation' },
  { id: 'nav-notifications', label: 'Notifications', description: 'Email & SMS templates', icon: Bell, href: '/notifications', section: 'navigation' },
  { id: 'nav-jobs', label: 'Jobs & Queues', description: 'Background job dashboard', icon: Play, href: '/jobs', section: 'navigation' },
  { id: 'nav-settings', label: 'Settings', description: 'Organization & roles', icon: Settings, href: '/settings', section: 'navigation' },
];

// ─── Component ───
export function CommandPalette() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Result[]>([]);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const searchTimer = useRef<ReturnType<typeof setTimeout>>();

  // ─── Keyboard shortcut: Cmd+K or Ctrl+K ───
  useEffect(() => {
    const handleGlobal = (e: globalThis.KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
      if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', handleGlobal);
    return () => window.removeEventListener('keydown', handleGlobal);
  }, [open]);

  // ─── Focus input when opened ───
  useEffect(() => {
    if (open) {
      setQuery('');
      setResults([]);
      setSelectedIdx(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // A scoped role must not be able to jump outside its section from here. The palette lists the
  // whole app's navigation, so without this it stays a working "go anywhere" control even once the
  // sidebar and router are locked down — and it leaks the app's structure to a restricted user.
  const permissions = useAuthStore((s) => s.user?.permissions);
  const partnerId = useAuthStore((s) => s.user?.partnerId);
  const scoped = useMemo(() => getScopedProfile(permissions, partnerId), [permissions, partnerId]);
  const navItems = useMemo(
    () => (scoped ? NAV_ITEMS.filter((n) => isPathAllowed(n.href, scoped)) : NAV_ITEMS),
    [scoped],
  );

  // ─── Search logic ───
  const doSearch = useCallback(async (q: string) => {
    const trimmed = q.trim().toLowerCase();

    // Filter navigation
    const navResults = trimmed.length > 0
      ? navItems.filter((n) =>
          n.label.toLowerCase().includes(trimmed) ||
          (n.description?.toLowerCase().includes(trimmed))
        )
      : navItems.slice(0, 6);

    // Search subscribers from API (debounced, min 2 chars)
    let subscriberResults: SubscriberResult[] = [];
    if (trimmed.length >= 2) {
      setSearching(true);
      try {
        const { data: resp } = await api.get(`/subscribers?search=${encodeURIComponent(trimmed)}&pageSize=8`);
        const items = resp.data?.items ?? resp.data ?? [];
        subscriberResults = items.map((s: {
          id: string;
          firstName: string;
          lastName: string;
          subscriberCode: string;
          phone: string;
          status: string;
          planDisplayName?: string;
        }) => ({
          id: `sub-${s.id}`,
          label: `${s.firstName} ${s.lastName}`,
          description: `${s.subscriberCode} · ${s.phone}${s.planDisplayName ? ` · ${s.planDisplayName}` : ''}`,
          icon: User,
          href: `/subscribers/${s.id}`,
          section: 'subscribers' as const,
          status: s.status,
        }));
      } catch {
        // Silently fail subscriber search
      } finally {
        setSearching(false);
      }
    }

    setResults([...subscriberResults, ...navResults]);
    setSelectedIdx(0);
  }, []);

  // ─── Debounced search ───
  useEffect(() => {
    if (!open) return;
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => doSearch(query), query.trim().length >= 2 ? 300 : 50);
    return () => clearTimeout(searchTimer.current);
  }, [query, open, doSearch]);

  // ─── Navigate to result ───
  const handleSelect = (result: Result) => {
    setOpen(false);
    navigate(result.href);
  };

  // ─── Keyboard navigation ───
  const handleKeyDown = (e: ReactKE<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIdx((prev) => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIdx((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && results[selectedIdx]) {
      e.preventDefault();
      handleSelect(results[selectedIdx]!);
    }
  };

  // ─── Scroll selected into view ───
  useEffect(() => {
    const el = listRef.current?.children[selectedIdx] as HTMLElement | undefined;
    el?.scrollIntoView({ block: 'nearest' });
  }, [selectedIdx]);

  if (!open) return null;

  // ─── Group results by section ───
  const subscriberResults = results.filter((r) => r.section === 'subscribers');
  const navResults = results.filter((r) => r.section === 'navigation');

  let flatIdx = -1; // Track flat index for highlight

  const statusDot = (status?: string) => {
    if (!status) return null;
    const color = status === 'active' ? 'bg-success' : status === 'suspended' ? 'bg-warning' : status === 'expired' ? 'bg-danger' : 'bg-muted-foreground';
    return <span className={cn('h-2 w-2 rounded-full shrink-0', color)} />;
  };

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm animate-fade-in" onClick={() => setOpen(false)} />

      {/* Palette */}
      <div
        className="fixed inset-x-0 top-[15%] z-50 mx-auto w-full max-w-xl animate-fade-up px-4"
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
      >
        <div className="overflow-hidden rounded-2xl border bg-card shadow-2xl">
          {/* Search Input */}
          <div className="flex items-center gap-3 border-b px-4">
            <Search className="h-4.5 w-4.5 shrink-0 text-muted-foreground" />
            <input
              ref={inputRef}
              type="text"
              role="combobox"
              aria-expanded={results.length > 0}
              aria-controls="command-results"
              aria-activedescendant={results[selectedIdx] ? `cmd-result-${selectedIdx}` : undefined}
              aria-autocomplete="list"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search subscribers, pages, actions..."
              className="h-12 flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
            />
            {searching && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
            <button onClick={() => setOpen(false)} className="rounded-md p-1 text-muted-foreground hover:bg-accent">
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Results */}
          <div ref={listRef} id="command-results" role="listbox" aria-label="Search results" className="max-h-[360px] overflow-y-auto p-2">
            {results.length === 0 && query.trim().length > 0 && !searching && (
              <div className="py-8 text-center text-sm text-muted-foreground">
                No results for &ldquo;{query}&rdquo;
              </div>
            )}

            {/* Subscriber Results */}
            {subscriberResults.length > 0 && (
              <>
                <p className="px-3 py-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground/70">
                  Subscribers
                </p>
                {subscriberResults.map((result) => {
                  flatIdx++;
                  const idx = flatIdx;
                  const Icon = result.icon;
                  return (
                    <button
                      key={result.id}
                      id={`cmd-result-${idx}`}
                      role="option"
                      aria-selected={selectedIdx === idx}
                      onClick={() => handleSelect(result)}
                      onMouseEnter={() => setSelectedIdx(idx)}
                      className={cn(
                        'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors',
                        selectedIdx === idx ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-muted/50'
                      )}
                    >
                      <div className={cn(
                        'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
                        selectedIdx === idx ? 'bg-primary/20' : 'bg-muted/80'
                      )}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium truncate">{result.label}</span>
                          {statusDot((result as SubscriberResult).status)}
                        </div>
                        <p className="text-xs text-muted-foreground truncate">{result.description}</p>
                      </div>
                      <ArrowRight className={cn('h-3.5 w-3.5 shrink-0 transition-opacity',
                        selectedIdx === idx ? 'opacity-100 text-primary' : 'opacity-0'
                      )} />
                    </button>
                  );
                })}
              </>
            )}

            {/* Navigation Results */}
            {navResults.length > 0 && (
              <>
                <p className="px-3 py-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground/70">
                  {query.trim().length > 0 ? 'Pages' : 'Quick Navigation'}
                </p>
                {navResults.map((result) => {
                  flatIdx++;
                  const idx = flatIdx;
                  const Icon = result.icon;
                  return (
                    <button
                      key={result.id}
                      id={`cmd-result-${idx}`}
                      role="option"
                      aria-selected={selectedIdx === idx}
                      onClick={() => handleSelect(result)}
                      onMouseEnter={() => setSelectedIdx(idx)}
                      className={cn(
                        'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors',
                        selectedIdx === idx ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-muted/50'
                      )}
                    >
                      <div className={cn(
                        'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
                        selectedIdx === idx ? 'bg-primary/20' : 'bg-muted/80'
                      )}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-medium truncate block">{result.label}</span>
                        {result.description && (
                          <p className="text-xs text-muted-foreground truncate">{result.description}</p>
                        )}
                      </div>
                      <ArrowRight className={cn('h-3.5 w-3.5 shrink-0 transition-opacity',
                        selectedIdx === idx ? 'opacity-100 text-primary' : 'opacity-0'
                      )} />
                    </button>
                  );
                })}
              </>
            )}
          </div>

          {/* Screen reader live region */}
          <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
            {results.length > 0
              ? `${results.length} result${results.length !== 1 ? 's' : ''} found`
              : query.trim().length > 0 && !searching
                ? 'No results found'
                : ''}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between border-t px-4 py-2 text-[11px] text-muted-foreground">
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1">
                <kbd className="rounded border bg-muted px-1 py-0.5 font-mono text-[10px]">↑↓</kbd>
                Navigate
              </span>
              <span className="flex items-center gap-1">
                <kbd className="rounded border bg-muted px-1 py-0.5 font-mono text-[10px]">↵</kbd>
                Select
              </span>
              <span className="flex items-center gap-1">
                <kbd className="rounded border bg-muted px-1 py-0.5 font-mono text-[10px]">esc</kbd>
                Close
              </span>
            </div>
            <span>Type 2+ chars to search subscribers</span>
          </div>
        </div>
      </div>
    </>
  );
}
